import React, { useMemo, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BadgeCheck, MapPin, Globe, Phone, Mail, Users, Star, Grid, MessageSquare, Info, Loader2, MessageCircle } from 'lucide-react';
import { generateUsers, generateVendors, generateListings } from '../utils/mockData';
import { ListingCard } from '../components/marketplace/ListingCard';
import { ReviewsSection } from '../components/vendors/ReviewsSection';
import { cn } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { startConversation } from '../lib/chat';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { resolveAvatar, resolveCover } from '../constants/sampleImages';

export const VendorProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('COLLECTION');
  const [listingTab, setListingTab] = useState('ACTIVE');
  const [messageLoading, setMessageLoading] = useState(false);
  
  const vendor = useMemo(() => {
    const users = generateUsers(10);
    const vendors = generateVendors(users);
    return vendors.find(v => v.id === id) || vendors[0];
  }, [id]);

  const handleMessageVendor = async () => {
    if (!userProfile) {
      navigate('/login');
      return;
    }

    if (!vendor) return;

    setMessageLoading(true);
    try {
      // Use existing vendor userId or fallback to mock if needed
      const vendorUserId = vendor.userId || vendor.id;
      
      const vendorUserDoc = await getDoc(doc(db, 'users', vendorUserId));
      let vendorData;
      
      if (vendorUserDoc.exists()) {
        vendorData = vendorUserDoc.data();
      } else {
        vendorData = {
          uid: vendorUserId,
          displayName: vendor.companyName,
          photoURL: vendor.logo
        };
      }

      await startConversation(userProfile, {
        uid: vendorData.uid,
        displayName: vendorData.displayName,
        photoURL: vendorData.photoURL
      });

      navigate('/messages');
    } catch (error) {
      console.error("Error starting conversation:", error);
    } finally {
      setMessageLoading(false);
    }
  };

  const allListings = useMemo(() => {
    return generateListings([vendor], 12);
  }, [vendor]);

  const activeListings = allListings.filter(l => l.status === 'ACTIVE');
  const soldListings = allListings.filter(l => l.status === 'SOLD');

  if (!vendor) return <div className="py-20 text-center font-bold text-slate-500">Loading vendor profile...</div>;

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <div className="relative h-64 md:h-80 rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
        <img src={resolveCover(vendor.coverImage)} className="w-full h-full object-cover" alt="Cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
        
        <div className="absolute bottom-4 md:bottom-6 left-4 md:left-10 right-4 flex flex-col md:flex-row items-start md:items-end gap-3 md:gap-6">
          <div className="w-16 h-16 md:w-32 md:h-32 rounded-xl md:rounded-2xl bg-white p-0.5 md:p-1 shadow-2xl border border-white/20 relative shrink-0">
            <img src={resolveAvatar(vendor.logo)} className="w-full h-full object-contain rounded-lg md:rounded-xl" alt="Logo" />
            {vendor.verified && (
              <div className="absolute -top-1 -right-1 md:-top-2 md:-right-2 bg-white rounded-full p-0.5 shadow-md">
                <BadgeCheck size={16} className="text-primary md:w-6 md:h-6" />
              </div>
            )}
          </div>
          <div className="pb-1 text-white min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h1 className="text-xl md:text-3xl font-black tracking-tight truncate">{vendor.companyName}</h1>
              {vendor.verificationLevel === 'GOLD' && (
                <span className="bg-amber-400 text-black text-[8px] md:text-[10px] font-black px-2 py-0.5 rounded uppercase flex items-center gap-1 shadow-lg">
                  <Star size={10} fill="currentColor" /> Gold Member
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] md:text-sm text-gray-300 font-medium">
              <span className="flex items-center gap-1"><Users size={12} className="md:w-[14px] md:h-[14px]" /> {vendor.followersCount} Followers</span>
              <span className="flex items-center gap-1"><Star size={12} className="text-amber-400 fill-amber-400 md:w-[14px] md:h-[14px]" /> {vendor.rating} Ratings</span>
              <span className="flex items-center gap-1 truncate"><MapPin size={12} className="md:w-[14px] md:h-[14px]" /> {vendor.location}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* About Section */}
        <div className="lg:col-span-4 space-y-6">
          <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
            <h2 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Info size={20} className="text-primary" />
              About Company
            </h2>
            <p className="text-sm text-gray-600 leading-relaxed mb-6">
              {vendor.description || "Leading gemstone exporter and dealer specializing in ethically sourced Sri Lankan sapphires and rare gems. Experience the finest minerals from the heart of Ratnapura."}
            </p>
            
            <div className="space-y-4 pt-4 border-t border-gray-50">
              <div className="flex items-center gap-3 text-sm text-gray-600">
                <Mail size={18} className="text-gray-400" />
                <span>{vendor.contactEmail}</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-600">
                <Phone size={18} className="text-gray-400" />
                <span>{vendor.phone}</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-600">
                <Globe size={18} className="text-gray-400" />
                <a href={vendor.website} className="text-primary hover:underline truncate">{vendor.website?.replace('https://', '')}</a>
              </div>
            </div>

            <div className="flex flex-col gap-2 mt-8">
              <button className="w-full py-2 bg-primary text-white rounded-lg font-bold text-sm shadow-sm hover:shadow-md transition-all">Follow Business</button>
              <div className="flex gap-2">
                <button 
                  onClick={handleMessageVendor}
                  disabled={messageLoading}
                  className="flex-1 py-2 bg-slate-900 text-white rounded-lg font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-70"
                >
                  {messageLoading ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <MessageCircle size={16} />
                  )}
                  {messageLoading ? 'Starting Chat...' : 'Message Seller'}
                </button>
                <button 
                  onClick={() => setActiveTab('REVIEWS')}
                  className={cn(
                    "p-2 rounded-lg border transition-all",
                    activeTab === 'REVIEWS' ? "bg-primary/10 border-primary text-primary" : "bg-gray-50 text-gray-500 border-gray-100 hover:bg-gray-100"
                  )}
                >
                  <MessageSquare size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content Tabs */}
        <div className="lg:col-span-8 space-y-6">
          <div className="flex items-center gap-6 border-b border-gray-100 px-2">
            <button 
              onClick={() => setActiveTab('COLLECTION')}
              className={cn(
                "pb-3 text-sm font-bold uppercase tracking-widest transition-all",
                activeTab === 'COLLECTION' ? "text-primary border-b-2 border-primary" : "text-gray-400 hover:text-gray-600"
              )}
            >
              Collection
            </button>
            <button 
              onClick={() => setActiveTab('REVIEWS')}
              className={cn(
                "pb-3 text-sm font-bold uppercase tracking-widest transition-all",
                activeTab === 'REVIEWS' ? "text-primary border-b-2 border-primary" : "text-gray-400 hover:text-gray-600"
              )}
            >
              Reviews
            </button>
          </div>

          {activeTab === 'COLLECTION' ? (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <button 
                  onClick={() => setListingTab('ACTIVE')}
                  className={cn(
                    "text-xs font-bold px-4 py-2 rounded-full transition-all",
                    listingTab === 'ACTIVE' ? "bg-slate-900 text-white" : "bg-white text-slate-500 border border-slate-100"
                  )}
                >
                  Available ({activeListings.length})
                </button>
                <button 
                  onClick={() => setListingTab('SOLD')}
                  className={cn(
                    "text-xs font-bold px-4 py-2 rounded-full transition-all",
                    listingTab === 'SOLD' ? "bg-slate-900 text-white" : "bg-white text-slate-500 border border-slate-100"
                  )}
                >
                  Sold Archive ({soldListings.length})
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {(listingTab === 'ACTIVE' ? activeListings : soldListings).map(listing => (
                  <ListingCard key={listing.id} listing={listing} />
                ))}
              </div>

              {(listingTab === 'ACTIVE' ? activeListings : soldListings).length === 0 && (
                <div className="py-20 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                  <p className="text-slate-400 font-medium">No {listingTab.toLowerCase()} items found.</p>
                </div>
              )}
            </div>
          ) : (
            <ReviewsSection vendorId={vendor.id} />
          )}
        </div>
      </div>
    </div>
  );
};
